<script lang="ts" setup>
import type { ChapterConfig } from '../configs/types'
import { getChapterConfig } from '../configs/brands'
import { getDailyPrice, getSavePercent, getUnitPriceValue, shouldShowDailyPrice, shouldShowPreferentialTotal } from '../utils/subscribeDailyPrice'
import { SubscriptionCycle } from '@/utils/enum'
import { VIP_TYPE } from '@/constants'
import { useI18n } from '@/composables/useI18n'

// 数据分支与默认组件完全一致(折扣会员 / 畅读会员 / 免费 / 无优惠 / 有优惠 / 覆写文案 / SAVE / 角标),仅重排版式
defineProps<{
  item: any
  getUnitPrice: (item: any) => string | number
  isZero: (value: any) => boolean
  dark?: boolean
}>()

const { t } = useI18n()
const config: ChapterConfig = getChapterConfig()

const popularTagBg = config.colors.pinkGradient
const crownBg = config.colors.pinkGradient
const cycleGradient = config.colors.purpleTextGradient
</script>

<template>
  <div class="nm-sub-card">
    <!-- 角标 -->
    <div v-if="item.cornerTip === 1 && item.marketingCopy" class="nm-sub-chip">
      {{ item.marketingCopy }}
    </div>

    <!-- 折扣会员 -->
    <div v-if="item.subscribeType === VIP_TYPE.DISCOUNT" class="nm-sub-row">
      <div class="nm-sub-icon">
        <i-lucide-crown class="nm-crown" />
      </div>

      <div class="nm-sub-main">
        <div class="nm-sub-title">
          {{ item.discountCoins }} Coins
        </div>
        <ul class="nm-sub-list">
          <li>
            <BasicFitText class="nm-sub-desc">
              {{ t('SET', { 0: item.discountOnceCoins }) }}
            </BasicFitText>
          </li>
          <li>
            <BasicFitText class="nm-sub-desc">
              {{ t('SET2', { 0: item.discountBonusCoins, 1: item.discountDailyCoins }) }}
            </BasicFitText>
          </li>
        </ul>
      </div>

      <div class="nm-sub-price">
        <div v-if="item.cycleStr" class="nm-price-cycle">
          {{ item.cycleStr }}
        </div>
        <div class="nm-price-main">
          ${{ item.preferentialPrice || item.price }}
        </div>
        <div class="nm-price-origin">
          ${{ item.discountOriginalPrice }}
        </div>
      </div>
    </div>

    <!-- 畅读会员 -->
    <div v-else class="nm-sub-row">
      <div class="nm-sub-icon">
        <i-lucide-crown class="nm-crown" />
      </div>

      <div class="nm-sub-main">
        <div class="nm-sub-head">
          <BasicFitText class="nm-sub-title">
            {{ SubscriptionCycle.getItemByValue(item.cycle)?.label }}
          </BasicFitText>
          <div v-if="shouldShowDailyPrice(item, isZero)" class="nm-save">
            SAVE {{ getSavePercent(item) }}%
          </div>
        </div>

        <BasicFitLines
          v-if="item.subscribeTextOverridden"
          :text="item.subscribeText"
          class="nm-sub-desc"
        />
        <div v-else class="nm-sub-desc nm-clamp-2">
          {{ item.subscribeText }}
        </div>
      </div>

      <!-- 免费 -->
      <div v-if="isZero(item.preferentialPrice)" class="nm-sub-price">
        <div class="nm-price-main">
          {{ config.texts.payTemplateFree }}
        </div>
        <div class="nm-price-origin">
          ${{ item.price }}
        </div>
      </div>

      <!-- 无优惠价:突出日单价 -->
      <div v-else-if="!item.preferentialPrice" class="nm-sub-price">
        <div class="nm-price-main">
          <template v-if="getUnitPriceValue(item)">
            ${{ getUnitPriceValue(item) }}<span class="nm-price-unit"> / Day</span>
          </template>
          <template v-else>
            ${{ item.price }}
          </template>
        </div>
        <div v-if="getUnitPriceValue(item)" class="nm-price-sub">
          ${{ item.price }}
        </div>
      </div>

      <!-- 有优惠价 -->
      <div v-else class="nm-sub-price">
        <div class="nm-price-main">
          <template v-if="shouldShowDailyPrice(item, isZero)">
            ${{ getDailyPrice(item) }}<span class="nm-price-unit"> / Day</span>
          </template>
          <template v-else>
            ${{ item.preferentialPrice }}
          </template>
        </div>
        <div class="nm-price-sub">
          <span class="nm-price-origin">${{ item.price }}</span>
          <span v-if="shouldShowPreferentialTotal(item, isZero)" class="nm-price-total">${{ item.preferentialPrice }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.nm-sub-card {
  position: relative;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  padding: 12px;
  text-align: left;
}

.nm-sub-chip {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  min-width: 46px;
  height: 16px;
  padding: 0 6px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  background: v-bind(popularTagBg);
  border-radius: 14px 0 10px 0;
  font-size: 9px;
  font-weight: 800;
  line-height: 1;
  color: #FFFFFF;
  white-space: nowrap;
}

.nm-sub-row {
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
}

/* 左侧渐变圆角方块 38 / r12 + crown 白 20 */
.nm-sub-icon {
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border-radius: 12px;
  background: v-bind(crownBg);
  display: flex;
  align-items: center;
  justify-content: center;
}

.nm-crown {
  width: 20px;
  height: 20px;
  color: #FFFFFF;
}

.nm-sub-main {
  flex: 1;
  min-width: 0;
  margin-left: 10px;
}

.nm-sub-head {
  display: flex;
  align-items: center;
}

.nm-sub-title {
  font-family: 'Baloo 2', 'Nunito Sans', system-ui, sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
  color: #2B1B34;
}

.nm-save {
  flex-shrink: 0;
  margin-left: 6px;
  padding: 2px 6px;
  border-radius: 4px;
  background: rgba(240, 80, 155, 0.14);
  color: #F0509B;
  font-size: 10px;
  font-weight: 700;
  line-height: 10px;
}

.nm-sub-desc {
  margin-top: 2px;
  font-size: 11px;
  font-weight: 400;
  line-height: 14px;
  color: #7C4DE8;
}

.nm-sub-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.nm-sub-price {
  flex-shrink: 0;
  margin-left: 8px;
  text-align: right;
}

.nm-price-main {
  font-family: 'Baloo 2', 'Nunito Sans', system-ui, sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 22px;
  color: #2B1B34;
  white-space: nowrap;
}

.nm-price-unit {
  font-family: 'Nunito Sans', system-ui, sans-serif;
  font-size: 11px;
  font-weight: 400;
  color: #AB9BB8;
}

.nm-price-sub {
  font-size: 11px;
  line-height: 14px;
  color: #AB9BB8;
}

.nm-price-origin {
  font-size: 11px;
  line-height: 14px;
  color: #AB9BB8;
  text-decoration: line-through;
}

.nm-price-total {
  margin-left: 4px;
  color: #2B1B34;
  font-weight: 700;
  text-decoration: none;
}

/* 折扣会员周期:随价格列常规排版(原右上角大字水印会压住价格) */
.nm-price-cycle {
  font-family: 'Baloo 2', 'Nunito Sans', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 700;
  line-height: 15px;
  background: v-bind(cycleGradient);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
}

.nm-clamp-2 {
  width: 100%;
  word-break: break-all;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
